import './styles.scss';

import { Badge, Card, Col, Container, Row } from 'react-bootstrap';

import BubbleTitle from '@/features/DriveTimeTool/components/BubbleTitle';
import Controls from '@/features/DriveTimeTool/components/Controls';
import DriveTimeDeviations from '@/features/DriveTimeTool/components/DriveTimeDeviations';
import DriveTimeScatterPlot from '@/features/DriveTimeTool/components/DriveTimeScatterPlot';
import { DtrLocation } from '@/models/gen/graphql';
import Feature from '@/common/Feature';
import History from '@/features/DriveTimeTool/components/History';
import ManifestUpload from '@/features/DriveTimeTool/components/ManifestUpload';
import { ManifestWithRoutes } from './api/services';
import RouteMap from './components/RouteMap';
import TimeFrameModal from '@/features/DriveTimeTool/components/TimeFrameModal';
import TimeIntervalStats from '@/features/DriveTimeTool/components/TimeIntervalStats';
import { createComponentState } from '@/state';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { useEffect } from 'react';

export type DriveTimeToolState = {
  loading: {
    history: boolean;
    locations: boolean;
    intervals: boolean;
    scatterPlot: boolean;
    deviations: boolean;
  };
  history: ManifestWithRoutes[];
  locations: Map<string, DtrLocation>;
  groupedLocationIdsByAddress: Map<string, string[]>;
  selected: string | null;
  selectedFrom?: 'manifestHistoryRouteId' | 'queryHistoryId';
  timeFrameId: string | null;
  showTimeFrameModal: boolean;
  expandHistory: boolean;
};

export const useDriveTimeTool = createComponentState<DriveTimeToolState>({
  loading: {
    history: false,
    locations: false,
    intervals: false,
    scatterPlot: false,
    deviations: false,
  },
  history: [],
  locations: new Map(),
  groupedLocationIdsByAddress: new Map(),
  selected: null,
  selectedFrom: null,
  timeFrameId: null,
  showTimeFrameModal: false,
  expandHistory: false,
});

const DriveTimeTool = (): JSX.Element => {
  useDocumentTitle('Drive Time Tool');

  useEffect(() => {
    // reset to default state on component unmount
    return () => {
      useDriveTimeTool.getState().setState({
        loading: {
          history: false,
          locations: false,
          intervals: false,
          scatterPlot: false,
          deviations: false,
        },
        history: [],
        locations: new Map(),
        groupedLocationIdsByAddress: new Map(),
        selected: null,
        selectedFrom: null,
        timeFrameId: null,
        showTimeFrameModal: false,
        expandHistory: false,
      });
    };
  }, []);

  return (
    <Feature name="DriveTimeTool">
      <Container fluid>
        <Row>
          <Col>
            <div className="drive-time-header">
              <h1 className="page-title text-primary">DRIVE TIME REPORT</h1>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="rounded-4 shadow">
              <Card.Body>
                <Row>
                  <Col className="text-center">
                    <BubbleTitle subtitle="Select one of the two options below to begin creating your drive time analysis.">
                      Choose Your Data Source
                    </BubbleTitle>
                  </Col>
                </Row>
                <Row>
                  <Col className="DriveTimeTool-Controls flex-grow-1">
                    <Controls />
                  </Col>
                  <Col className="divider d-flex flex-column justify-content-center align-items-center gap-2 flex-grow-0 p-0">
                    <div className="vr align-self-auto" />
                    <Badge bg="none" pill text="dark">
                      OR
                    </Badge>
                    <div className="vr align-self-auto" />
                  </Col>
                  <Col className="DriveTimeTool-ManifestUpload flex-grow-1">
                    <ManifestUpload />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col className="DriveTimeTool-History">
            <History />
          </Col>
          <Col className="DriveTimeTool-MapDisplay">
            <RouteMap />
          </Col>
        </Row>
        <Row>
          <Col className="DriveTimeTool-TimeIntervalStats">
            <TimeIntervalStats />
          </Col>
        </Row>
        <Row>
          <Col className="DriveTimeTool-Chart">
            <DriveTimeScatterPlot />
          </Col>
        </Row>
        <Row>
          <Col className="DriveTimeTool-Chart">
            <DriveTimeDeviations />
          </Col>
        </Row>
        <Row>
          <Col className="DriveTimeTool-Footer">
            <span className="text-muted">Powered by SkyHop Technologies</span>
          </Col>
        </Row>
      </Container>
      <TimeFrameModal />
    </Feature>
  );
};

export default DriveTimeTool;
